/**
 * Countdown Timer Component
 * Shows countdown for OTP resend functionality
 */

import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

const CountdownTimer = ({
  seconds = 60,
  onComplete,
  autoStart = true,
  showIcon = true
}) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const [isActive, setIsActive] = useState(autoStart);

  useEffect(() => {
    let interval = null;

    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            setIsActive(false);
            if (onComplete) onComplete();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, timeLeft, onComplete]);

  const reset = (newSeconds = seconds) => {
    setTimeLeft(newSeconds);
    setIsActive(true);
  };

  const stop = () => {
    setIsActive(false);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const secs = time % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const isExpired = timeLeft === 0;

  return (
    <div className="flex items-center justify-center gap-2">
      {showIcon && !isExpired && (
        <Clock className="w-4 h-4 text-gray-500" />
      )}
      <span
        className={`text-sm font-medium ${
          isExpired ? 'text-gray-400' : 'text-gray-700'
        }`}
      >
        {isExpired ? (
          'Time expired'
        ) : (
          <>
            Resend OTP in <span className="font-bold">{formatTime(timeLeft)}</span>
          </>
        )}
      </span>
    </div>
  );
};

// Export both component and hook for programmatic control
export const useCountdownTimer = (initialSeconds = 60) => {
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    let interval = null;

    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, timeLeft]);

  const start = (seconds = initialSeconds) => {
    setTimeLeft(seconds);
    setIsActive(true);
  };

  const stop = () => {
    setIsActive(false);
  };

  const reset = () => {
    setTimeLeft(initialSeconds);
    setIsActive(false);
  };

  return {
    timeLeft,
    isActive,
    isExpired: timeLeft === 0,
    start,
    stop,
    reset
  };
};

export default CountdownTimer;
